/* eslint-disable react/no-danger */
/**
 * Module dependencies.
 */

import { CiClock2, CiLocationOn, CiMail, CiMap, CiPhone } from 'react-icons/ci';
import { Company } from 'types/company';
import {
  ContactCardTitle,
  ContactContainer,
  MapsLinkWrapper,
} from './contacts-info.style';
import { ContactInfoField } from './contact-info-field';
import { EmailLink } from 'components/atoms/email-link/email-link';
import { JSX, useMemo } from 'react';
import { Language } from 'types/language';
import { Link } from 'components/atoms/link/link';
import { Phones } from '../phones/phones';
import { ResolveLocaleString } from '../resolve-locale-string/resolve-locale-string';
import { Stack } from '@mui/material';
import { useTranslation } from 'next-i18next';
import MarkdownIt from 'markdown-it';

/**
 * `Props` type.
 */

type Props = {
  company: Company;
  languages: Language[];
};

/**
 * Md instance.
 */

const md = new MarkdownIt();

/**
 * Export `ContactsInfo` component.
 */

export function ContactsInfo({ company, languages }: Props): JSX.Element {
  const [translate] = useTranslation();
  const address = useMemo(() => {
    return md.render(company.address);
  }, [company.address]);

  return (
    <ContactContainer>
      <ContactCardTitle>{translate('contacts.details.title')}</ContactCardTitle>

      <Stack spacing={2}>
        {address && (
          <ContactInfoField
            icon={<CiLocationOn />}
            label={translate('contacts.details.address')}
            raw
            value={
              <>
                <div
                  dangerouslySetInnerHTML={{ __html: address }}
                  style={{ fontSize: 14 }}
                />

                <Link
                  href={`https://www.google.com/maps?q=${company.latitude},${company.longitude}`}
                  target={'_blank'}
                >
                  <MapsLinkWrapper>
                    <CiMap /> {translate('contacts.details.seeOnMap')}
                  </MapsLinkWrapper>
                </Link>
              </>
            }
          />
        )}

        {(company.phonePT || company.phoneFR || company.phoneEN) && (
          <ContactInfoField
            icon={<CiPhone />}
            label={translate('contacts.details.phone')}
            raw
            value={
              <Phones
                languages={languages}
                phoneEN={company.phoneEN}
                phoneFR={company.phoneFR}
                phonePT={company.phonePT}
              />
            }
          />
        )}

        {company.email && (
          <ContactInfoField
            icon={<CiMail />}
            label={translate('contacts.details.email')}
            value={<EmailLink email={company.email} />}
          />
        )}

        {company.schedule && (
          <ContactInfoField
            icon={<CiClock2 />}
            label={translate('contacts.details.schedule')}
            value={
              <ResolveLocaleString value={company.schedule}>
                {(value) => String(value)}
              </ResolveLocaleString>
            }
          />
        )}
      </Stack>
    </ContactContainer>
  );
}
